/**
 * OnboardingTooltip — One-time contextual tip for first-time users.
 *
 * Renders a small banner/callout that can be dismissed permanently.
 * Uses useTooltipDismissed hook to persist dismissal in localStorage.
 *
 * Props:
 *   id       — unique key for this tooltip (persisted in localStorage)
 *   message  — text to show
 *   icon     — optional emoji or icon string (default: 💡)
 *   style    — optional inline styles for positioning
 *
 * Usage:
 *   <OnboardingTooltip
 *     id="grocery-copy-list"
 *     message="Tap 'Copy List' or 'Send to Notes' to take your list to the store."
 *   />
 */
import React from 'react';
import { X } from 'lucide-react';
import { useTooltipDismissed } from '../../hooks/useTooltipDismissed';

interface OnboardingTooltipProps {
  id: string;
  message: string;
  icon?: string;
  style?: React.CSSProperties;
}

const OnboardingTooltip: React.FC<OnboardingTooltipProps> = ({
  id, message, icon = '💡', style,
}) => {
  const [dismissed, dismiss] = useTooltipDismissed(id);

  if (dismissed) return null;

  return (
    <div
      className="onboarding-tooltip"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        padding: '12px 16px',
        marginBottom: '12px',
        backgroundColor: '#EFF6FF',
        border: '1px solid #BFDBFE',
        borderRadius: '8px',
        fontSize: '0.875rem',
        color: '#1E40AF',
        lineHeight: 1.5,
        animation: 'tooltipFadeIn 0.3s ease-out',
        ...style,
      }}
    >
      <span style={{ fontSize: '1rem', flexShrink: 0, marginTop: '1px' }}>{icon}</span>
      <span style={{ flex: 1 }}>{message}</span>
      <button
        onClick={dismiss}
        aria-label="Dismiss tip"
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: '#93A3B8',
          padding: '2px',
          flexShrink: 0,
          marginTop: '-2px',
        }}
      >
        <X size={14} />
      </button>
    </div>
  );
};

export default OnboardingTooltip;
